import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import {
  ensureIndex,
  appendChapters,
  searchVerses,
  classifyQuery,
  type SearchHit,
  type QueryMeta,
} from '../search/local-search.js';
import { currentChapter, appendedChapters } from '../state/chapter-store.js';
import { route } from '../state/store.js';
import { atlasHomeUrl, resolveAtlasLang } from '@fcbh/atlas-schema';
import { publisher } from '../publisher/mode.js';
import { colorProfile } from '../state/color-profile.js';
import { refsForVerse, prettyRef, crossRefsLoaded } from '../data/cross-refs.js';

export const askOpen = signal(false);
export const askSeedRef = signal<string | null>(null);

// Expanded starter prompts — thematic, topical, and relational.
const PROMPTS = [
  'Show me verses on hope',
  'Verses about God\'s mercy',
  'Where is Jerusalem?',
  'Who was Paul?',
  'Show me verses on peace',
  'What does the Bible say about prayer?',
];

type Turn =
  | { kind: 'user'; text: string }
  | { kind: 'reply'; query: string; hits: SearchHit[]; meta: QueryMeta };

export function ChatBubble() {
  const open = askOpen.value;
  return open ? <ChatPanel onClose={() => (askOpen.value = false)} /> : null;
}


type SeedVerse = { ref: string; text: string; label: string };

function ChatPanel({ onClose }: { onClose: () => void }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [seedVerse, setSeedVerse] = useState<SeedVerse | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Seed the index with the chapter(s) the reader has loaded.
  useEffect(() => {
    const current = currentChapter.value;
    const appended = appendedChapters.value;
    const chapters = [
      ...(current ? [current] : []),
      ...appended,
    ];
    if (chapters.length === 0) return;
    ensureIndex(chapters);
    appendChapters(chapters);
  }, [currentChapter.value, appendedChapters.value]);

  // Honor a seed ref — store the verse for display rather than auto-submitting.
  useEffect(() => {
    const seed = askSeedRef.value;
    if (!seed) return;
    askSeedRef.value = null;
    const current = currentChapter.value;
    if (!current) return;
    const verse = current.verses.find((v) => v.ref === seed);
    if (!verse) return;
    const parts = seed.split('.');
    const label = parts.length >= 3
      ? `${current.bookName} ${parts[1]}:${parts[2]}`
      : seed;
    setSeedVerse({ ref: seed, text: verse.text, label });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function submit(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    const meta = classifyQuery(trimmed);
    const hits = meta.kind === 'scripture' ? searchVerses(trimmed, 12) : [];
    setTurns((prev) => [
      ...prev,
      { kind: 'user', text: trimmed },
      { kind: 'reply', query: trimmed, hits, meta },
    ]);
    setDraft('');
  }

  const onScrim = (e: MouseEvent) => {
    if ((e.target as HTMLElement) === (e.currentTarget as HTMLElement)) onClose();
  };

  const chapter = currentChapter.value;
  const indexSize = chapter
    ? (chapter.verses.length + appendedChapters.value.reduce((n, c) => n + c.verses.length, 0))
    : 0;

  return (
    <div class="modal-scrim" onClick={onScrim} role="dialog" aria-label="Scripture chat">
      <div class="modal-sheet modal-sheet--tall">
        <div class="modal-sheet-grabber" />

        {/* Header */}
        <div class="modal-sheet-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div class="chat-modal-header-mark">
              {/* sparkle icon */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
              </svg>
            </div>
            <div>
              <div class="chat-modal-name">Scripture Chat</div>
              <div class="chat-modal-sub">Grounded in your Bible</div>
            </div>
          </div>
          <button type="button" class="modal-sheet-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Chat scroll area */}
        <div class="chat-scroll-area" ref={scrollRef}>
          {turns.length === 0 ? (
            <GreetingView onSubmit={submit} indexSize={indexSize} seedVerse={seedVerse} />
          ) : (
            turns.map((turn, i) =>
              turn.kind === 'user' ? (
                <UserBubble key={i} text={turn.text} />
              ) : (
                <ReplyBubble key={i} query={turn.query} hits={turn.hits} meta={turn.meta} />
              ),
            )
          )}
        </div>

        {/* Quick-action chips */}
        {turns.length > 0 ? (
          <div class="chat-chip-row">
            {['More like this', 'Add to plan', 'Read aloud'].map((c) => (
              <button key={c} type="button" class="chat-chip-ghost" onClick={() => setDraft(c)}>
                {c}
              </button>
            ))}
          </div>
        ) : null}

        {/* Input pill */}
        <div class="chat-input-wrap">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(draft);
            }}
          >
            <div class="chat-input-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
              </svg>
              <input
                type="text"
                class="chat-input-field"
                placeholder="Ask anything…"
                value={draft}
                onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
                aria-label="Ask about this chapter"
                autocomplete="off"
              />
              <button
                type="submit"
                class={`chat-input-send${draft.trim() ? ' has-text' : ''}`}
                aria-label="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="6" y1="18" x2="18" y2="6"/>
                  <polyline points="11 6 18 6 18 13"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function GreetingView({
  onSubmit,
  indexSize,
  seedVerse,
}: {
  onSubmit: (text: string) => void;
  indexSize: number;
  seedVerse: SeedVerse | null;
}) {
  // Subscribe so cross-ref data triggers a re-render when it loads.
  void crossRefsLoaded.value;
  const crossRefs = seedVerse ? refsForVerse(seedVerse.ref) : [];

  return (
    <>
      {seedVerse ? (
        <div class="chat-seed-verse">
          <blockquote class="chat-seed-quote">"{seedVerse.text}"</blockquote>
          <span class="chat-seed-ref">{seedVerse.label}</span>
          {crossRefs.length > 0 ? (
            <div class="chat-seed-refs">
              <div class="chat-bubble-lead">Related passages</div>
              <ul class="chat-seed-ref-list">
                {crossRefs.slice(0, 6).map((r) => (
                  <li key={r.to}>
                    <button
                      type="button"
                      class="chat-seed-ref-row"
                      onClick={() => {
                        const parts = r.to.split('.');
                        const book = parts[0];
                        const ch = Number(parts[1]);
                        if (book && ch) {
                          route.value = { book: book.toUpperCase(), chapter: ch };
                          askOpen.value = false;
                        }
                      }}
                    >
                      <span class="chat-seed-ref-label">{prettyRef(r.to)}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
              <p class="chat-seed-attribution">Cross-references © OpenBible.info · CC BY 4.0</p>
            </div>
          ) : null}
        </div>
      ) : (
        <div class="chat-greeting">
          <p class="chat-greeting-line">
            Ask about a theme, a person, a place, or a topic. I search{' '}
            {indexSize > 0 ? `${indexSize.toLocaleString()} verses` : 'the loaded chapters'}{' '}
            and point you to Atlas for people and places.
          </p>
        </div>
      )}
      <div class="chat-prompts">
        {PROMPTS.map((p) => (
          <button key={p} type="button" class="chat-chip-ghost" onClick={() => onSubmit(p)}>
            {p}
          </button>
        ))}
      </div>
    </>
  );
}

function UserBubble({ text }: { text: string }) {
  return <div class="chat-bubble-user">{text}</div>;
}

function ReplyBubble({
  query,
  hits,
  meta,
}: {
  query: string;
  hits: SearchHit[];
  meta: QueryMeta;
}) {
  // Atlas query — redirect to Atlas in-context.
  if (meta.kind === 'atlas') {
    const term = meta.detail ?? query;
    const pub = publisher.peek();
    const atlasLang = resolveAtlasLang([pub?.bcp47, pub?.languageIso]);
    const atlasHref = atlasHomeUrl({
      lang: atlasLang,
      from: 'fcbh-reader',
      palette: colorProfile.peek(),
      ...(typeof window !== 'undefined' ? { returnTo: window.location.href } : {}),
    });
    return (
      <div class="chat-bubble-assistant">
        <div class="chat-bubble-lead">Atlas · people &amp; places</div>
        <p style={{ margin: '0 0 10px', fontSize: '13.5px', lineHeight: '1.5' }}>
          {`"${term}"`} is covered in the Atlas — the biblical geography and history reference
          that pairs with this reader.
        </p>
        <a
          href={atlasHref}
          target="_blank"
          rel="noreferrer"
          class="chat-atlas-link"
        >
          Open Atlas{meta.detail ? ` — search "${meta.detail}"` : ''} ↗
        </a>
        {/* Also show any scripture hits as a bonus. */}
        {hits.length > 0 ? (
          <>
            <div class="chat-bubble-lead" style={{ marginTop: '12px' }}>
              Related verses
            </div>
            <VerseList hits={hits.slice(0, 4)} />
          </>
        ) : null}
      </div>
    );
  }

  // Module query — surface the connected module.
  if (meta.kind === 'module') {
    const moduleLabel: Record<string, string> = {
      trauma: 'Trauma &amp; Healing',
      healing: 'Healing',
      evangelism: 'Evangelism',
      discipleship: 'Discipleship',
      pastoral: 'Pastoral Training',
    };
    const label = (meta.detail && moduleLabel[meta.detail]) ?? meta.detail ?? 'this module';
    return (
      <div class="chat-bubble-assistant">
        <div class="chat-bubble-lead">Connected module</div>
        <p style={{ margin: '0 0 10px', fontSize: '13.5px', lineHeight: '1.5' }}>
          The {label} module in this app covers {query.toLowerCase()} in depth.
          Look for it in the menu.
        </p>
        {hits.length > 0 ? (
          <>
            <div class="chat-bubble-lead" style={{ marginTop: '12px' }}>
              Scripture starting points
            </div>
            <VerseList hits={hits.slice(0, 6)} />
          </>
        ) : null}
      </div>
    );
  }

  // Scripture query — standard verse results.
  if (hits.length === 0) {
    return (
      <div class="chat-bubble-assistant">
        <div class="chat-bubble-lead">No matches</div>
        Nothing in the chapters you have open matches &ldquo;{query}&rdquo;. Navigate to
        more chapters and search again, or try a different phrase.
      </div>
    );
  }

  const concepts = useMemo(() => {
    const set = new Set<string>();
    for (const h of hits) for (const c of h.concepts) set.add(c);
    return [...set].slice(0, 5);
  }, [hits]);

  return (
    <div class="chat-bubble-assistant">
      <div class="chat-bubble-lead">
        {hits.length} match{hits.length === 1 ? '' : 'es'}
        {concepts.length > 0 ? <> · {concepts.join(' · ')}</> : null}
      </div>
      <VerseList hits={hits.slice(0, 8)} />
    </div>
  );
}

/**
 * Shared verse list — each item navigates the reader to that chapter when tapped.
 * The reader stays open; only the route changes.
 */
function VerseList({ hits }: { hits: SearchHit[] }) {
  function navigateTo(hit: SearchHit): void {
    // Parse OSIS ref (e.g. "ROM.8.15") to extract book and chapter.
    const parts = hit.ref.split('.');
    const book = parts[0];
    if (!book) return;
    // Navigate the reader — this changes the chapter in-app without leaving.
    route.value = { book: book.toUpperCase(), chapter: hit.chapter };
    // Close the chat panel after navigation so the verse is visible.
    askOpen.value = false;
    // Scroll to the verse after a short frame so the chapter has mounted.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const esc =
          typeof CSS !== 'undefined' && CSS.escape
            ? CSS.escape(hit.ref)
            : hit.ref.replace(/["\\]/g, '\\$&');
        const el = document.querySelector(`[data-verse-ref="${esc}"]`) as HTMLElement | null;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  return (
    <ol class="chat-reply-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {hits.map((r) => (
        <li key={r.ref}>
          <div
            class="chat-verse-ref"
            onClick={() => navigateTo(r)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigateTo(r); }}
            title={`Go to ${r.bookName} ${r.chapter}:${r.verseNumber}`}
          >
            <div class="chat-verse-ref-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div class="chat-ref-name">{r.bookName} {r.chapter}:{r.verseNumber}</div>
              <div class="chat-ref-quote">"{r.text}"</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </li>
      ))}
    </ol>
  );
}
